<template>
  <div id="floorplan-detail">
    <MoleculesLoadingOverlay v-if="loading" />
    <div v-else>
      <OrganismsProductDetailBW :organism="brandWindow" />
      <OrganismsFlexibleFeatures
        v-if="flexibleFeatures.item && flexibleFeatures.item.length"
        :organism="flexibleFeatures"
      />
      <OrganismsTabbedContent
        v-if="tabbedContent.defaultTabHash"
        :organism="tabbedContent"
      >
        <template v-for="item in tabbedContent.item" :key="item.id" #[item.id]>
          <img
            class="mx-auto mb-5"
            :src="item.image.src"
            :alt="item.image.alt"
          />
          <MoleculesButtons
            :buttons="tabbedContent.button"
            :button-colors="tabbedContent.button.colors"
            class="!justify-center mx-auto"
          />
        </template>
      </OrganismsTabbedContent>
      <OrganismsGalleryGrid
        id="featured-photos-gallery"
        v-if="galleryGrid"
        class="mx-auto my-24 bg-gray-900"
        :organism="galleryGrid"
      />
      <div
        v-if="viewSlideshow.slideshowEmbed"
        class="px-2 pt-24 pb-12 mx-auto bg-bc-gray view-a-slideshow"
      >
        <h2
          v-if="viewSlideshow.title1"
          class="mb-8 text-2xl text-center md:text-3xl lg:text-5xl theme-text-2xl"
        >
          {{ viewSlideshow.title1 }}
        </h2>
        <div class="pb-16" v-html="viewSlideshow.slideshowEmbed"></div>
        <!--<MoleculesButtons
          :buttons="viewSlideshow.buttons"
          :button-colors="viewSlideshow.buttons.buttonColors"
          class="uppercase"
        />-->
      </div>
      <OrganismsVirtualTour
        id="virtual-tour"
        v-if="virtualTour.virtualTourUrl"
        class="mx-auto my-24 bg-gray-900"
        :organism="virtualTour"
      />
      <OrganismsPopularHomes v-if="popularHomes.items.length" :organism="popularHomes" pageType="plan" :dynamicName="planName"/>
      <OrganismsTextCTA id="cta-be-the-first" :organism="beTheFirst" />
      <OrganismsModalForm>
        <header class="mb-4">
          <h2 class="mb-4 text-4xl font-semibold text-black font-archer">
            Be the First
          </h2>
          <p v-if="!isFormSubmitted" class="text-lg text-gray-500">
            Please provide us your information and we will get back to you.
            Check 'Become a Boulder Creek Insider' to receieve updates regarding
            this plan.
          </p>
        </header>
        <FormsHubSpotForm @submit="isFormSubmitted = true" :form="form" />
      </OrganismsModalForm>
      <OrganismsPopularPlans
        v-if="popularPlans.items.length"
        :organism="popularPlans"
      />
      <div class="block h-24 bg-gradient-to-b from-bc-gray to-white"></div>
    </div>

    <OrganismsModalLogin />
  </div>
</template>

<style>
#floorplan-detail {
  .text-cta {
    @apply mb-20;
    p {
      @apply leading-8;

      a {
        @apply text-sm text-bc-dark-blue underline;
      }
    }

    .buttons a {
      @apply uppercase;
    }
  }

  .virtual-tour {
    h2 {
      @apply text-white;
    }
  }

  .tabbed-content {
    header {
      background-image: linear-gradient(101deg, #367ea0 0%, #4dadce 97%);
      .header-content {
        @apply max-w-[556px];
      }
    }
    .tabs-component-tabs {
      @apply mx-auto flex-col md:flex-row justify-center;

      .tabs-component-tab {
        @apply md:max-w-[190px];

        .tabs-component-tab-a {
          @apply whitespace-nowrap;
        }
      }
    }
    .tabs-component-panels {
      .buttons a {
        @apply text-[15px] uppercase font-ubuntu font-medium py-3 px-10;
      }
    }
  }

  .popular-plans, .popular-homes {
    .product-grid {
      @apply w-full;
    }
  }

  

  .view-a-slideshow {
    iframe {
      @apply mx-auto w-[1038px] max-w-full h-80 sm:h-[696px] overflow-hidden;
    }
    .buttons {
      @apply justify-center;
    }
  }
  .virtual-tour {
    @apply my-0;
    h2 {
      @apply text-white;
    }
  }
}
</style>

<script setup>
const route = useRoute();
import { useAsyncQuery } from "#imports";
import { GET_PLAN } from "~/graphql/GetPlan";
const { data, pending, error } = await useAsyncQuery(GET_PLAN, {
  uri: route.path,
});
import { storeToRefs } from "pinia";
const breadcrumbs = useBreadcrumbs();
const { breadcrumbsList } = storeToRefs(breadcrumbs);
const modalForm = useModalForm();
const isFormSubmitted = ref(false);

const loading = pending;

const plan = computed(() => {
  return data?.value?.plan ?? [];
});

console.log("plan", plan.value);

const plans = computed(() => {
  return data?.value?.plans?.nodes ?? [];
});

const planName = computed(() => {
  return plan?.value?.floorplanDetails?.displayTitle;
});

const homes = computed(() => {
  return data?.value?.homes?.nodes ?? [];
});

console.log("homes", homes.value);

// Get the collection of the model
const planCollection = computed(() => {
  return plan.value?.floorplanDetails?.collection;
});

const planTitle = computed(() => {
  return plan.value?.title;
});

console.log("planTitle", planTitle.value);

// Filter homes based on the model's collection
const filteredHomes = computed(() => {
  return homes.value.filter(home => home?.qmiHomeDetails?.floorplan?.title === planTitle.value);
});

//console.log(filteredHomes.value);

// Shuffle and limit to 3 items
const popularHomes = computed(() => {
  const shuffledHomes = filteredHomes.value.sort(() => 0.5 - Math.random());
  return {
    backgroundColor: "#ffffff",
    items: shuffledHomes.slice(0, 3),
  };
});

//console.log("popularHomes", popularHomes);

const collectionFeatures = computed(() => {
  let parsedJSON = {};
  try {
    parsedJSON = data?.value?.collectionFeatures
      ? JSON.parse(data?.value?.collectionFeatures)
      : {};
  } catch (e) {
    // console.log("Invalid collectionFeatures JSON:", e);
    return {};
  }
  return parsedJSON;
});

const computedBreadcrumbs = computed(() => {
  return [
    {
      title: plan?.value?.floorplanDetails?.neighborhood?.title
        ? plan?.value?.floorplanDetails?.neighborhood?.title +
          ", " +
          plan?.value?.floorplanDetails?.neighborhood?.neighborhood?.city
        : "",
      link: plan?.value?.floorplanDetails?.neighborhood?.uri,
    },
    {
      title: "Floorplans",
      link: plan?.value?.floorplanDetails?.neighborhood?.uri + "#floorplans",
    },
    {
      title: plan?.value?.title + " Plan",
    },
  ];
});

watch(computedBreadcrumbs, () => {
  breadcrumbsList.value = computedBreadcrumbs.value;
});

const brandWindow = computed(() => {
  return {
    collection: plan?.value?.floorplanDetails?.collection,
    title: plan?.value?.floorplanDetails?.displayTitle,
    // title: plan?.value?.title,
    type: plan?.value?.floorplanDetails?.type,
    neighborhoodTitle: plan?.value?.floorplanDetails?.neighborhood?.title,
    neighborhoodLink: plan?.value?.floorplanDetails?.neighborhood?.uri,
    bedsMin: plan?.value?.floorplanDetails?.bedroomsMin,
    bedsMax: plan?.value?.floorplanDetails?.bedroomsMax,
    bathsMin: plan?.value?.floorplanDetails?.bathroomsMin,
    bathsMax: plan?.value?.floorplanDetails?.bathroomsMax,
    garageMin: plan?.value?.floorplanDetails?.garageMin,
    garageMax: plan?.value?.floorplanDetails?.garage,
    sqftMin: plan?.value?.floorplanDetails?.mainSqrFt,
    sqftMax: plan?.value?.floorplanDetails?.finishedSqrFt,
    priceMin: plan?.value?.floorplanDetails?.basePrice,
    autoplaySlider: true,
    autoplayDelay: 10000,
    sliderImages: plan?.value?.floorplanDetails?.elevations,
    button1: true,
    button1Text: "Get Brochure",
    button1Target: "_blank",
    link1: plan?.value?.floorplanDetails?.pdfEPub ?? false,
    hasFavorites: true,
    id: plan?.value?.databaseId,
    thumbnail: getImageSrc(
      plan?.value?.floorplanDetails?.elevations[0],
      "medium",
    ),
  };
});

const tabbedContent = computed(() => {
  const tabs = Array.isArray(plan?.value?.floorplanDetails?.tabs)
    ? plan.value.floorplanDetails.tabs.map((tab, currentIndex, array) => {
        let tabName = tab.title;

        array.forEach(function (checkTab, checkIndex, checkArray) {
          if (tab.title === checkTab.title) {
            if (currentIndex > checkIndex) {
              tabName = tab.title + " - Opt";
            }
          }
        });

        const adjustedIndex = currentIndex + 1;

        return {
          name: tabName,
          id: kebabCase(tabName) + "-tab-" + adjustedIndex,
          image: {
            src: getImageSrc(tab.image, "large"),
            alt: tab.image.altText,
          },
        };
      })
    : [];

  return {
    containerWidth: "default",
    title1: "Explore this floorplan",
    title2: plan?.value?.title,
    item: tabs,
    defaultTabHash: tabs[0]?.id,
    variant: "product-detail",
    button: {
      colors: ["button-yellow"],
      button1: true,
      button1Text: "Get Brochure",
      button1Target: "_blank",
      link1: plan?.value?.floorplanDetails?.pdfEPub,
    },
  };
});

const beTheFirst = computed(() => {
  return {
    title1: "Be the First",
    description: `<p>Be the first to know about our Colorado neighborhoods and homes, including early access to pricing, new home plans and VIP events.</p>`,
    button1: true,
    button1Text: "Get Updates",
  };
});

const flexibleFeatures = computed(() => {
  const collection = plan?.value?.floorplanDetails?.collection;
  let collectionTitle = "";
  let featuresList = [];

  switch (true) {
    case collection?.toLowerCase().includes("easyhouse"):
      collectionTitle = "easyHouse®";
      featuresList = collectionFeatures?.value?.easyHouse ?? [];
      break;
    case collection?.toLowerCase().includes("wee-cottage"):
      collectionTitle = "wee-Cottage®";
      featuresList = collectionFeatures?.value?.weeCottage ?? [];
      break;
    case collection?.toLowerCase().includes("limited-edition"):
      collectionTitle = "Limited Edition";
      featuresList = collectionFeatures?.value?.limitedEdition ?? [];
      break;
    case collection?.toLowerCase().includes("new-beginnings"):
      collectionTitle = "New Beginnings";
      featuresList = collectionFeatures?.value?.newBeginnings ?? [];
      break;
  }

  return {
    title1: collectionTitle + " Features",
    item: featuresList,
  };
});

const popularPlans = computed(() => {
  // Check if specific floorplans are provided
  const specificFloorplans = plan?.value?.floorplanDetails?.popularPlans ?? [];

  // If specific floorplans exist, use them; otherwise, use the default logic
  const items = specificFloorplans.length > 0
    ? specificFloorplans
    : getPopularPlans(plan?.value, plans?.value);

  return {
    backgroundColor: "#f0f0f0",
    items,
  };
});

const galleryGrid = computed(() => {
  const galleryItems = Array.isArray(
    plan?.value?.floorplanDetails?.galleryItems,
  )
    ? plan?.value?.floorplanDetails?.galleryItems.map((item) => item.image)
    : [];

  return { gallery: galleryItems, thumbnailCount: 10, backgroundColor: "#fff" };
});

const virtualTour = computed(() => {
  const collection = plan?.value?.floorplanDetails?.collection ?? "";
  const collectionSpace = collection ? collection + " " : "";

  const showButton1 = false;

  return {
    title1: plan?.value?.floorplanDetails?.matterportTourTitle,
    title2: "Tour this " + collectionSpace + " home",
    virtualTourEmbedType: "url",
    virtualTourUrl: plan?.value?.floorplanDetails?.matterportTour,
    button1: showButton1,
    button1Text: "Learn More",
    link1: "#null",
    buttonColors: ["button-gray"],
  };
});

const viewSlideshow = computed(() => {
  const showButton1 = plan?.value?.floorplanDetails?.pdfEPub ? true : false;
  return {
    title1: plan?.value?.floorplanDetails?.matterportTourTitle
      ? "Step inside the " + plan?.value?.floorplanDetails?.matterportTourTitle
      : "",
    slideshowEmbed: plan?.value?.floorplanDetails?.virtualTour,
    buttons: {
      button1: showButton1,
      button1Text: "Get Brochure",
      button1Target: "_blank",
      link1: plan?.value?.floorplanDetails?.pdfEPub,
      buttonColors: ["button-yellow"],
    },
  };
});

onMounted(() => {
  window.addEventListener(
    "hashchange",
    () => {
      if (window.location.hash === "#modal-form") {
        modalForm.openModal();
        window.location.hash = "";
      }
    },
    false,
  );

  addTriggers();
});

const addTriggers = () => {
  const modalFormTriggers = document.querySelectorAll(
    "#cta-be-the-first .button-1, #cta-be-the-first a[href='#modal-form']",
  );

  modalFormTriggers.forEach((node) => {
    // Clone & replace node to remove existing event listeners
    const clonedNode = node.cloneNode(true);
    node.parentNode.replaceChild(clonedNode, node);

    clonedNode.addEventListener("click", (event) => {
      // Add event listener
      event.preventDefault();
      modalForm.openModal();
    });
  });
};

onUpdated(() => {
  addTriggers();
});

const form = computed(() => {
  return {
    id: "ca2c18ce-092f-4156-aecd-257fb7776d83",
    hiddenFields: [
      {
        inputSelector: 'input[name="floorplan_of_interest"]',
        inputValue: plan?.value?.floorplanDetails?.hubspotValue ?? "",
      },
    ],
  };
});

watch(loading, async (newLoading, oldLoading) => {
  const urlHash = window.location.hash;

  if (urlHash && oldLoading && !newLoading) {
    await nextTick();
    document.querySelector(urlHash).scrollIntoView(true);
  }
});
</script>
